import { createReducer, on } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { SecurityGroup, SecurityGroupType } from './security-group.model';
import * as Actions from './security-group.actions';
import { SortDirection, QueryParams, queryParamsFromResult } from 'src/app/modules/core/store/models/query.model';
import { GUID_EMPTY } from '@app/core/constants';
import { SecurityGroupMember } from './security-group-member.model';
import { SecurityGroupPermissionSetItem } from './security-group-permission-set-item.model';

export const securityGroupFeatureKey = 'security_groups';

export interface State extends EntityState<SecurityGroup> {
  queryParams?: QueryParams;
  memberListQueryParams?: QueryParams;
  members?: SecurityGroupMember[];
  permissionSets?: SecurityGroupPermissionSetItem[];
  selectedSecurityGroupUId?: string | null;
  selectedSecurityGroupUIdForDelete?: string | null;
  securityGroupTypeFilter: SecurityGroupType | null;
  searchTerm: string | null;
  err?: Error;
  ui?: {
    showAddMembersForm?: boolean;
    showSecurityGroupForm?: boolean;
    editSection?: string | null;
    isDirty?: boolean;
    isPermissionSetsDirty?: boolean;
  };
}

export const adapter: EntityAdapter<SecurityGroup> = createEntityAdapter<SecurityGroup>({
  selectId: ({ uId }: SecurityGroup) => uId
});

export const initialState: State = adapter.getInitialState({
  queryParams: {
    skip: 0,
    take: 10,
    totalCount: 0,
    sortField: null,
    sortDirection: SortDirection.Ascending,
    where: 'Type!=2'
  },
  memberListQueryParams: {
    skip: 0,
    take: 10,
    totalCount: 0
  },
  members: [],
  permissionSets: [],
  selectedSecurityGroupUId: null,
  selectedSecurityGroupUIdForDelete: null,
  searchTerm: null,
  securityGroupTypeFilter: null,
  err: null,
  ui: {
    showAddMembersForm: false,
    showSecurityGroupForm: false,
    editSection: 'permissions',
    isDirty: false,
    isPermissionSetsDirty: false
  }
});

export const reducer = createReducer(
  initialState,
  on(Actions.loadSecurityGroups, (state, action) =>
    adapter.setAll(action.results.data, {
      ...state,
      queryParams: queryParamsFromResult(action.results),
      memberListQueryParams: {
        skip: 0,
        take: 10,
        totalCount: 0
      },
      members: [],
      permissionSets: [],
      selectedSecurityGroupUId: null,
      err: null,
      ui: {
        ...state.ui,
        isDirty: false
      }
    })
  ),
  on(Actions.showSecurityGroupForm, (state, { showSecurityGroupForm }) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        showSecurityGroupForm
      }
    };
  }),
  on(Actions.showAddMembersForm, (state, { showAddMembersForm }) => ({
    ...state,
    ui: {
      ...state.ui,
      showAddMembersForm
    }
  })),
  on(Actions.cancelEditSecurityGroupComplete, state => ({
    ...state,
    selectedSecurityGroupUId: null
  })),
  on(Actions.setSelectedSecurityGroupComplete, (state, { uId }) => ({
    ...state,
    selectedSecurityGroupUId: uId,
    err: null,
    ui: {
      ...state.ui,
      editSection: 'permissions'
    }
  })),
  on(Actions.setSelectedEditSection, (state, action) => ({
    ...state,
    ui: {
      ...state.ui,
      editSection: action.sectionName
    }
  })),
  on(Actions.setSecurityGroupForDelete, (state, { uId }) => ({
    ...state,
    selectedSecurityGroupUIdForDelete: uId
  })),
  on(Actions.clearSecurityGroupForDelete, state => ({
    ...state,
    selectedSecurityGroupUIdForDelete: null
  })),
  on(Actions.newSecurityGroup, state => {
    const newSecurityGroup: SecurityGroup = {
      uId: GUID_EMPTY,
      displayName: '',
      securityGroupMembers: []
    };
    return adapter.addOne(newSecurityGroup, {
      ...state,
      selectedSecurityGroupUId: GUID_EMPTY,
      err: null,
      ui: {
        showSecurityGroupForm: true
      }
    });
  }),
  on(Actions.addSecurityGroup, (state, { securityGroup }) => adapter.setOne(securityGroup, state)),
  on(Actions.updateSecurityGroup, (state, { securityGroup }) =>
    adapter.updateOne(
      {
        id: securityGroup.uId,
        changes: {
          ...securityGroup
        }
      },
      { ...state, ui: { ...state.ui, isDirty: false } }
    )
  ),
  on(Actions.removeSecurityGroup, (state, { uId }) => adapter.removeOne(uId, state)),
  on(Actions.loadSelectedSecurityGroupMembers, (state, { results: { data: members, ...results } }) => ({
    ...state,
    members,
    memberListQueryParams: queryParamsFromResult(results),
    err: null
  })),
  on(Actions.loadPermissionSets, (state, { permissionSets }) => ({
    ...state,
    permissionSets,
    err: null
  })),
  on(Actions.setPermissionSetItems, (state, { permissionSets }) => ({
    ...state,
    permissionSets,
    ui: {
      ...state.ui,
      isPermissionSetsDirty: true
    }
  })),
  on(Actions.savePermissionSetsComplete, state => ({
    ...state,
    ui: {
      ...state.ui,
      isPermissionSetsDirty: false
    }
  })),
  on(Actions.clearPermissionSet, state => ({
    ...state,
    permissionSets: []
  })),
  on(Actions.addMembersComplete, state => ({
    ...state,
    ui: {
      ...state.ui,
      showAddMembersForm: false
    },
    err: null
  })),
  on(Actions.searchSecurityGroups, (state, { searchTerm }) => ({
    ...state,
    searchTerm,
    queryParams: {
      ...state.queryParams,
      skip: 0
    }
  })),
  on(Actions.changeTypeFilter, (state, { securityGroupTypeFilter }) => ({
    ...state,
    securityGroupTypeFilter,
    queryParams: {
      ...state.queryParams,
      skip: 0
    }
  })),
  on(Actions.updateQueryParams, (state, { updates }) => ({
    ...state,
    queryParams: {
      ...state.queryParams,
      ...updates
    }
  })),
  on(Actions.loadSelectedSecurityGroup, (state, { securityGroup }) =>
    adapter.upsertOne(securityGroup, {
      ...state,
      selectedSecurityGroupUId: securityGroup.uId
    })
  )
);

export const { selectIds, selectEntities, selectAll, selectTotal } = adapter.getSelectors();

export const getQueryParams = (state: State) => state?.queryParams;

export const getSearchTerm = (state: State) => state?.searchTerm;

export const getMemberListQueryParams = (state: State) => state?.memberListQueryParams;

export const getShowSecurityGroupForm = (state: State) => state?.ui?.showSecurityGroupForm;

export const getShowAddMembersForm = (state: State) => state?.ui?.showAddMembersForm;

export const getSelectedSecurityGroup = ({ selectedSecurityGroupUId: uId, entities }: State) => (!!uId ? entities[uId] : null);

export const getSecurityGroupForDelete = ({ selectedSecurityGroupUIdForDelete: uId, entities }: State) => (!!uId ? entities[uId] : null);

export const getSecurityGroupTypeFilter = (state: State) => state?.securityGroupTypeFilter;

export const getEditSection = (state: State) => {
  const section = state?.ui?.editSection;
  return section || 'permissions';
};

export const getSecurityGroupMembers = ({ members }: State) => members;

export const getSecurityGroupPermissionSets = ({ permissionSets }: State) => permissionSets;

export const getIsDirty = (state: State) => state?.ui?.isDirty;

export const getIsPermissionSetsDirty = (state: State) => state?.ui?.isPermissionSetsDirty;
